<script setup>
import TooltipInfo from "@/components/global-themed/TooltipInfo.vue";
import { computed } from 'vue';
import { shortNumber } from '@/composables/useNumberFormatter';

const props = defineProps({
  stat: {
    type: Object,
    required: true,
  },
});

const isPositive = computed(() => props.stat.differencePercentage > 0);
const isNeutral = computed(() => props.stat.differencePercentage === 0);

const belowThreshold = computed(() => props.stat.differencePercentage < -5);

const statBoxText = computed(() => {
  if (belowThreshold.value) {
    return `Last ${props.stat.daysCompared} Days`
  }

  return `${ isPositive.value ? '↑' : '↓' } ${ Math.abs(props.stat.differencePercentage) }% m/o/m`
})

const statBoxClass = computed(() => {
  if (belowThreshold.value) {
    return 'tw-text-neutral-700'
  }
  return isPositive.value ? 'tw-text-success-700' : 'tw-text-danger-700'
})

const hasReportingPeriod = computed(() => props.stat.periodFrom && props.stat.periodTo);

const tooltipText = computed(() => {
  if (!hasReportingPeriod.value) {
    return props.stat.description;
  }

  return `
    ${props.stat.description}
    <br/>
    <br/>
    <strong>Current reporting period:</strong>
    <br/>
    ${props.stat.periodFrom} - ${props.stat.periodTo}
    <br/>
    <br/>
    Metrics are based on the last 30 complete days of available data. Due to processing and validation time, data is typically available up to yesterday rather than the current day.`;
});
</script>

<template>
  <div class="tw-border tw-border-neutral-300 tw-rounded-xl tw-p-5 tw-flex tw-gap-x-4 tw-items-center">
    <span class="tw-text-neutral-900 tw-text-[80px] tw-self-center tw-leading-none">
      {{ shortNumber(stat.currentValue) }}
    </span>
    <div class="tw-flex tw-flex-col tw-gap-y-3 tw-w-full">
      <div class="tw-flex tw-justify-between tw-gap-2">
        <span class="tw-text-neutral-900 tw-text-lg tw-leading-snug">{{ stat.title }}</span>

        <TooltipInfo
          class="tw-justify-self-end tw-shrink-0"
          :text="tooltipText"
          :html="hasReportingPeriod"
        />
      </div>

      <span
        v-if="!isNeutral && stat.difference !== null"
        :class="statBoxClass"
        class="tw-text-base"
      >
        {{ statBoxText }}
      </span>
    </div>
  </div>
</template>
